import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Meld } from '../core/types';
import CardComponent from './CardComponent';
import StackCardComponent from './StackCardComponent';
import '../styles/MeldDisplay.css';

interface MeldDisplayProps {
  melds: Meld[];
  isCompact?: boolean;
}

const MeldDisplay: React.FC<MeldDisplayProps> = ({ melds, isCompact = false }) => {
  const [previewMeld, setPreviewMeld] = useState<Meld | null>(null);
  useEffect(() => {
    if (!previewMeld) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPreviewMeld(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [previewMeld]);

  if (!melds || melds.length === 0) {
    return <div className="meld-display-empty"></div>;
  }

  const getMeldTitle = (meld: Meld): string => {
    switch (meld.type) {
      case 'TI':
        return `提 ${meld.huxi}胡`;
      case 'PAO':
        return `跑 ${meld.huxi}胡`;
      case 'SAO':
        return `扫 ${meld.huxi}胡`;
      case 'KAN':
        return `坎 ${meld.huxi}胡`;
      case 'PENG':
        return `碰 ${meld.huxi}胡`;
      case 'CHI_123':
        return `123 ${meld.huxi}胡`;
      case 'CHI_2710':
        return `2710 ${meld.huxi}胡`;
      case 'CHI_SEQ':
        return '顺';
      case 'CHI_JIAO':
        return '绞';
      default:
        return `${meld.huxi}胡`;
    }
  };

  const getCompactTitle = (meld: Meld): string => {
    if (meld.type.startsWith('CHI_')) return '吃';
    return getMeldTitle(meld).split(' ')[0];
  };

  return (
    <div className={`meld-display ${isCompact ? 'compact' : ''}`}>
      {melds.map((meld, idx) => (
        <div
          key={meld.id || idx}
          className="meld-group"
          onClick={() => isCompact && setPreviewMeld(meld)}
          role={isCompact ? 'button' : undefined}
          tabIndex={isCompact ? 0 : undefined}
          onKeyDown={event => {
            if (isCompact && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              setPreviewMeld(meld);
            }
          }}
        >
          <div
            className="meld-cards"
            style={{
              height: isCompact
                ? `${40 + Math.max(meld.cards.length - 1, 0) * 10}px`
                : `${73 + Math.max(meld.cards.length - 1, 0) * 26}px`
            }}
          >
            {meld.cards.map((card, cIdx) => (
              <StackCardComponent
                key={card.id || cIdx}
                card={card}
                index={cIdx}
                total={meld.cards.length}
                isBack={(meld.type === 'TI' || meld.type === 'SAO') && cIdx < meld.cards.length - 1}
                isMicro={isCompact}
                isSmall={!isCompact}
                className="meld-card-wrapper"
              />
            ))}
            <div className="meld-tag" data-compact-title={getCompactTitle(meld)}>
              {getMeldTitle(meld)}
            </div>
          </div>
        </div>
      ))}
      {isCompact && previewMeld && createPortal(
        <div className="meld-preview-backdrop" onClick={() => setPreviewMeld(null)}>
          <div
            className="meld-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`${getMeldTitle(previewMeld)}牌面详情`}
            onClick={event => event.stopPropagation()}
          >
            <div className="meld-preview-header">
              <strong>{getMeldTitle(previewMeld)}</strong>
              <button type="button" className="meld-preview-close" onClick={() => setPreviewMeld(null)} aria-label="关闭牌面预览">
                ×
              </button>
            </div>
            <div className="meld-preview-cards">
              {previewMeld.cards.map((card, cardIndex) => (
                <CardComponent key={card.id || cardIndex} card={card} isSmall />
              ))}
            </div>
            <div className="meld-preview-names">
              {previewMeld.cards.map(card => `${card.isBig ? '大' : '小'}${card.name}`).join(' · ')}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MeldDisplay;